import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import EntityDetailLayout from '../../components/EntityDetailLayout';
import EnrichmentPanel from '../../components/enrichment/EnrichmentPanel';
import DeleteButton from '../../components/DeleteButton';
import EntityPivotLink from '../../components/EntityPivotLink';
import InvestigationGraph from '../../components/InvestigationGraph';
import PhoneVerdictPanel from '../../components/PhoneVerdictPanel';
import PromoteModal from '../../components/PromoteModal';
import RelationshipTable from '../../components/RelationshipTable';
import { deleteSearch, fetchSearch, runLiveCarrierLookup } from '../../services/intelligenceApi';

const TABS = [
  { id: 'summary', label: 'Summary' },
  { id: 'enrichment', label: 'Enrichment' },
  { id: 'relationships', label: 'Relationships' },
  { id: 'raw', label: 'Raw' },
];

function IntelligenceSearchResult() {
  const { queryId } = useParams();
  const navigate = useNavigate();
  const [search, setSearch] = useState(null);
  const [activeTab, setActiveTab] = useState('summary');
  const [loading, setLoading] = useState(true);
  const [promoteOpen, setPromoteOpen] = useState(false);
  const [graphOpen, setGraphOpen] = useState(false);
  const [promotedId, setPromotedId] = useState(null);
  const [liveLoading, setLiveLoading] = useState(false);

  useEffect(() => {
    let timer;
    const load = async () => {
      try {
        const res = await fetchSearch(queryId);
        setSearch(res.data);
        if (res.data.status === 'running' || res.data.status === 'pending') {
          timer = setTimeout(load, 2500);
        }
      } catch (err) {
        console.error(err);
        setSearch(null);
      } finally {
        setLoading(false);
      }
    };
    load();
    return () => timer && clearTimeout(timer);
  }, [queryId]);

  const enrichments = search?.enrichments || {};
  const enrichmentEntries = Object.entries(enrichments).filter(([key]) => key !== 'error');
  const isPhoneSearch = search?.detected_type === 'phone';

  const handleRunLiveCarrier = async () => {
    setLiveLoading(true);
    try {
      const res = await runLiveCarrierLookup(queryId);
      setSearch(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLiveLoading(false);
    }
  };

  const sidebar = search ? (
    <>
      <div className="entity-sidebar-section">
        <div className="entity-sidebar-title">Quick actions</div>
        <button type="button" className="btn btn-primary btn-sm" style={{ width: '100%', marginBottom: '8px' }} onClick={() => setPromoteOpen(true)}>
          Promote to indicator
        </button>
        <button type="button" className="btn btn-secondary btn-sm" style={{ width: '100%', marginBottom: '8px' }} onClick={() => setGraphOpen(true)}>
          Show graph
        </button>
        {isPhoneSearch && enrichments.live_carrier?.status !== 'success' && (
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            style={{ width: '100%' }}
            disabled={liveLoading}
            onClick={handleRunLiveCarrier}
          >
            {liveLoading ? 'Running…' : 'Run live carrier lookup'}
          </button>
        )}
      </div>
      {promotedId && (
        <div className="entity-sidebar-section">
          <div className="entity-sidebar-title">Promoted</div>
          <EntityPivotLink type="indicator" id={promotedId} label="View indicator" />
        </div>
      )}
    </>
  ) : null;

  return (
    <>
      <EntityDetailLayout
        entityType="search"
        title={search?.raw_input || 'Search'}
        subtitle={
          search
            ? `${search.detected_type} · ${search.status}${search.risk_score != null ? ` · risk ${Math.round(search.risk_score)}` : ''}`
            : ''
        }
        breadcrumbs={[
          { label: 'Intelligence', to: '/intelligence/search' },
          { label: 'History', to: '/intelligence/history' },
          { label: search?.raw_input || queryId },
        ]}
        tabs={TABS}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        loading={loading}
        notFound={!loading && !search ? 'Search not found.' : null}
        notFoundAction={<Link to="/intelligence/search" className="btn btn-secondary">Back to search</Link>}
        headerActions={
          search && (
            <>
              <span className={`status-badge ${search.status === 'complete' ? 'active' : 'draft'}`}>{search.status}</span>
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => setGraphOpen(true)}>Graph</button>
              <button type="button" className="btn btn-primary btn-sm" onClick={() => setPromoteOpen(true)}>Promote</button>
              <DeleteButton
                label="Delete"
                className="btn btn-sm btn-danger"
                confirmMessage={`Delete search "${search.raw_input}"?`}
                onDelete={async () => {
                  await deleteSearch(queryId);
                  navigate('/intelligence/history');
                }}
              />
            </>
          )
        }
        sidebar={sidebar}
      >
        {search && activeTab === 'summary' && (
          <>
            {isPhoneSearch && search.status === 'complete' && (
              <PhoneVerdictPanel
                enrichments={enrichments}
                onRunLiveCarrier={handleRunLiveCarrier}
                liveLoading={liveLoading}
              />
            )}
            <div className="card entity-overview-card">
              <div className="card-header">
                <div className="card-title">Basic information</div>
              </div>
              <div className="card-body">
                <div className="entity-kv-grid">
                  <div><strong>Input</strong><div><code>{search.raw_input}</code></div></div>
                  <div><strong>Normalized</strong><div><code>{search.normalized_input}</code></div></div>
                  <div><strong>Type</strong><div>{search.detected_type}</div></div>
                  <div><strong>Status</strong><div>{search.status}</div></div>
                  <div><strong>Risk</strong><div>{search.risk_score != null ? Math.round(search.risk_score) : '—'}</div></div>
                  <div><strong>Sections</strong><div>{enrichmentEntries.length}</div></div>
                </div>
                {search.status === 'running' && (
                  <p style={{ marginTop: '16px', color: 'var(--hdp-accent)' }}>Enrichment running — results update automatically.</p>
                )}
                {search.error_message && (
                  <div className="hdp-alert error" style={{ marginTop: '16px' }}>{search.error_message}</div>
                )}
              </div>
            </div>
          </>
        )}

        {search && activeTab === 'enrichment' && (
          <EnrichmentPanel
            enrichments={enrichments}
            detectedType={search.detected_type}
          />
        )}

        {search && activeTab === 'relationships' && (
          <div className="card">
            <div className="card-body">
              <RelationshipTable entityType="search" entityId={queryId} />
            </div>
          </div>
        )}

        {search && activeTab === 'raw' && (
          <div className="card">
            <div className="card-body">
              <pre className="entity-raw-json">{JSON.stringify(search, null, 2)}</pre>
            </div>
          </div>
        )}
      </EntityDetailLayout>

      <PromoteModal
        open={promoteOpen}
        onClose={() => setPromoteOpen(false)}
        sourceType="search"
        sourceId={queryId}
        sourceLabel={search?.raw_input}
        onPromoted={(indicator) => setPromotedId(indicator.indicator_id)}
      />

      <InvestigationGraph
        open={graphOpen}
        onClose={() => setGraphOpen(false)}
        entityType="search"
        entityId={queryId}
        title={search?.raw_input}
      />
    </>
  );
}

export default IntelligenceSearchResult;
